import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, CalendarDaysIcon, SparklesIcon, TrophyIcon } from '@heroicons/react/24/outline';
import VotingPageShell from '../components/voting/VotingPageShell';
import comingSoonImage from '../assets/coming_soon.jpg';

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Date unavailable' : date.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const getStatus = (competition) => {
  if (competition.status === 'completed' || new Date(competition.endDate) <= new Date()) return 'Closed';
  if (competition.status === 'active') return 'Active';
  return 'Upcoming';
};

const formatCategory = (category) => category
  ? `${category.charAt(0).toUpperCase()}${category.slice(1)}`
  : 'General';

function Voting() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/competitions`);
        const currentDate = new Date();
        const validCompetitions = response.data.filter((competition) => {
          const endDate = new Date(competition.endDate);
          return endDate > currentDate;
        });
        setCompetitions(validCompetitions);
      } catch (fetchError) {
        console.error('Error fetching competitions:', fetchError);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCompetitions();
  }, []);

  const handleCompetitionClick = (competitionId) => navigate(`/voting/${competitionId}`);

  return (
    <VotingPageShell>
      <div className="border-b border-line bg-surface-900">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
          <p className="eyebrow-accent inline-flex items-center gap-2">
            <SparklesIcon aria-hidden="true" className="h-4 w-4" /> COMMUNITY VOTING
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-2xl">
              <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Make your vote count</h1>
              <p className="mt-2 text-sm leading-6 text-gray-400 sm:text-base">
                Explore current campaigns and support the people and teams you believe in.
              </p>
            </div>
            <button type="button" onClick={() => navigate('/buy-votes')} className="btn-primary w-full gap-2 sm:w-auto">
              <TrophyIcon aria-hidden="true" className="h-4 w-4" /> Get vote credits
            </button>
          </div>
        </div>
      </div>

      <section aria-labelledby="campaigns-heading" className="mx-auto max-w-7xl px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Your community, your choice</p>
            <h2 id="campaigns-heading" className="mt-1 text-xl font-semibold tracking-tight text-white sm:text-2xl">
              Open campaigns
            </h2>
          </div>
          {!loading && !error && competitions.length > 0 && (
            <span className="rounded-lg border border-line bg-surface-850 px-3 py-1.5 text-sm text-gray-300">
              {competitions.length} {competitions.length === 1 ? 'campaign' : 'campaigns'}
            </span>
          )}
        </div>

        {loading ? (
          <div role="status" aria-label="Loading voting campaigns" className="flex min-h-64 items-center justify-center gap-3 rounded-xl border border-line bg-surface-900 text-sm text-gray-300">
            <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-surface-600 border-t-accent" />
            Loading campaigns…
          </div>
        ) : error ? (
          <div role="alert" className="rounded-xl border border-accent-dim bg-surface-900 p-8 text-center">
            <h3 className="text-lg font-semibold text-white">Campaigns are unavailable</h3>
            <p className="mt-2 text-sm text-gray-400">We couldn't load voting campaigns right now. Please try again later.</p>
          </div>
        ) : competitions.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {competitions.map((competition) => {
              const status = getStatus(competition);
              const active = status === 'Active';
              return (
                <button
                  type="button"
                  key={competition.id}
                  onClick={() => handleCompetitionClick(competition.id)}
                  className="group flex min-h-64 flex-col rounded-xl border border-line bg-surface-850 p-5 text-left transition duration-200 hover:-translate-y-0.5 hover:border-line-strong hover:bg-surface-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:p-6"
                >
                  <div className="flex w-full items-start justify-between gap-3">
                    <span className="inline-flex items-center gap-2 rounded-md border border-line bg-surface-900 px-2.5 py-1 text-xs font-medium text-gray-300">
                      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-emerald-400' : 'bg-gray-500'}`} />
                      {status}
                    </span>
                    <ArrowRightIcon aria-hidden="true" className="h-4 w-4 text-gray-500 transition group-hover:translate-x-0.5 group-hover:text-accent-soft" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold tracking-tight text-white sm:text-xl">{competition.title}</h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-6 text-gray-400">
                    {competition.description || 'Explore this campaign and show your support.'}
                  </p>
                  <div className="mt-auto grid grid-cols-2 gap-3 border-t border-line pt-4">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Category</p>
                      <p className="mt-1 truncate text-sm text-gray-200">{formatCategory(competition.category)}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                        <CalendarDaysIcon aria-hidden="true" className="h-3.5 w-3.5" /> Voting period
                      </p>
                      <p className="mt-1 text-sm text-gray-200">{formatDate(competition.startDate)} – {formatDate(competition.endDate)}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center rounded-xl border border-line bg-surface-900 px-6 py-10 text-center sm:py-12">
            <img src={comingSoonImage} alt="" className="mb-5 h-40 w-40 object-contain sm:h-48 sm:w-48" />
            <h3 className="text-xl font-semibold text-white">No open campaigns right now</h3>
            <p className="mt-2 max-w-md text-sm leading-6 text-gray-400">New voting opportunities will appear here when they open. Check back soon.</p>
          </div>
        )}
      </section>
    </VotingPageShell>
  );
}

export default Voting;
